import { useState, type FormEvent } from 'react';
import { CircleAlert, ShieldCheck } from 'lucide-react';
import { command, string } from './api';
import { Dialog, Field, Status } from './components';
import { instant } from './domain/canonical';
import type { WorkspaceProps } from './App';
import type { RecordItem } from './server/types';

export interface RightsDialogProps {
  candidate: RecordItem;
  close: () => void;
  run: WorkspaceProps['run'];
  busy: boolean;
}

const placements = ['Feed', 'Reel', 'Story', 'Carousel'] as const;
const read = (form: FormData, key: string) => String(form.get(key) ?? '').trim();
function utcTimestamp(value: string, label: string) {
  const stamp = `${value}:00Z`;
  if (!value || instant(stamp) === null) throw new Error(`Choose a valid ${label.toLowerCase()} in UTC.`);
  return stamp;
}

export function RightsDialog({ candidate, close, run, busy }: RightsDialogProps) {
  const [error, setError] = useState('');
  const [basis, setBasis] = useState('');
  const [startRule, setStartRule] = useState('');
  const [endRule, setEndRule] = useState('');
  const exportHash = string(candidate.data.hash);
  const exactExportAvailable = /^sha256:[a-f0-9]{64}$/.test(exportHash);
  const previous = candidate.data.rights && typeof candidate.data.rights === 'object' && !Array.isArray(candidate.data.rights)
    ? candidate.data.rights as Record<string, unknown> : null;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setError('');
    const ok = await run(async () => {
      try {
        if (!exactExportAvailable) throw new Error('This export needs a verified file fingerprint before its rights can be recorded.');
        const selectedPlacements = form.getAll('placements').map(String);
        if (!selectedPlacements.length) throw new Error('Choose at least one placement covered by the evidence.');
        const paid = read(form, 'paidUsage');
        if (!['owned', 'licensed'].includes(basis) || !['yes', 'no'].includes(paid)) throw new Error('Choose the rights basis and explicitly confirm whether paid use is covered.');
        if (!startRule || !endRule) throw new Error('Confirm the start and expiry terms from the evidence.');
        if (form.get('ownerAttested') !== 'on') throw new Error('Confirm that the permission covers this exact export before saving.');
        const start = startRule === 'dated' ? utcTimestamp(read(form, 'validFrom'), 'Rights start') : undefined;
        const end = endRule === 'dated' ? utcTimestamp(read(form, 'expiresAt'), 'Rights expiry') : undefined;
        if (end && Date.parse(end) <= Math.max(Date.now(), start ? Date.parse(start) : 0)) throw new Error('Rights expiry must follow the start and be in the future.');
        const holder = read(form, 'holder'), reference = read(form, 'evidenceReference'), details = read(form, 'evidenceDetails');
        if (!holder || !reference || !details) throw new Error('Record the rights holder, the evidence reference and the permission you checked.');
        const source = [
          `Rights holder: ${holder}`,
          `Evidence reference: ${reference}`,
          `Permission checked: ${details}`,
          `Start terms: ${start ?? 'Evidence explicitly permits use without a start restriction.'}`,
          `Expiry terms: ${end ?? 'Evidence explicitly permits use without an expiry date.'}`,
        ].join('\n');
        return await command('verify_candidate_rights', {
          status: basis,
          source,
          placements: selectedPlacements,
          paid_usage: paid === 'yes',
          ...(start ? { valid_from: start } : {}),
          ...(end ? { expires_at: end } : {}),
          asset_hash: exportHash,
          owner_attested: true,
        }, candidate);
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Rights could not be saved. Your entries are still here.');
        throw failure;
      }
    }, 'Usage rights recorded for this exact export. Related publication approvals must be reviewed again.');
    if (ok) close();
  };

  return <Dialog title="Review usage rights" close={close}>
    <form className="dialog-body" onSubmit={submit}>
      {error && <div className="note danger" role="alert"><CircleAlert size={17}/><span>{error}</span></div>}
      <div><h3>{candidate.title}</h3><p className="muted">Exact export · revision {candidate.revision}</p></div>
      <p>Record the permission you have checked. Creating or receiving an image does not, by itself, establish the right to publish it or use it in ads.</p>
      {!exactExportAvailable && <div className="note danger" role="status">This export has no verified file fingerprint. Reconnect or import the exact file before recording rights.</div>}
      {previous && <details><summary>Previously recorded rights</summary><p className="preserve-lines">{string(previous.source, 'Evidence details unavailable.')}</p><Status value={previous.exportHash === exportHash ? 'review' : 'hold'} label={previous.exportHash === exportHash ? 'Recorded for this export; review before replacing' : 'Recorded for a different export'}/></details>}

      <Field label="Rights basis">
        <select name="basis" required value={basis} onChange={event => setBasis(event.target.value)}>
          <option value="">Choose the basis supported by your evidence</option>
          <option value="owned">Lamora owns the necessary rights</option>
          <option value="licensed">Lamora has permission or a license</option>
        </select>
      </Field>
      <Field label="Rights holder" hint="Name the person or organization granting the rights, or the documented owner."><input name="holder" required maxLength={240}/></Field>
      <Field label="Evidence reference" hint="A license or release, signed agreement, permission email, or documented ownership record. Use a link or a precise file/thread reference."><input name="evidenceReference" required maxLength={1000}/></Field>
      <Field label="Permission you checked" hint="Describe what the evidence permits. Include any creator, model, music or third-party limitations relevant to this export."><textarea name="evidenceDetails" required minLength={10} maxLength={6000} rows={4}/></Field>

      <fieldset className="fidelity-checklist"><legend>Allowed placements</legend><p className="muted">Choose only the placements covered by the evidence.</p>{placements.map(placement => <label className="check-field" key={placement}><input type="checkbox" name="placements" value={placement}/>{placement}</label>)}</fieldset>
      <Field label="Does the permission cover paid advertising?">
        <select name="paidUsage" required defaultValue=""><option value="">Choose from the documented permission</option><option value="no">No — organic use only</option><option value="yes">Yes — paid use is explicitly covered</option></select>
      </Field>
      <div className="field-grid">
        <Field label="When may use begin?"><select required value={startRule} onChange={event => setStartRule(event.target.value)}><option value="">Choose the documented start terms</option><option value="dated">From a specific date and time</option><option value="unrestricted">Evidence permits use without a start restriction</option></select></Field>
        <Field label="When does permission expire?"><select required value={endRule} onChange={event => setEndRule(event.target.value)}><option value="">Choose the documented expiry terms</option><option value="dated">At a specific date and time</option><option value="unrestricted">Evidence explicitly permits use without expiry</option></select></Field>
        {startRule === 'dated' && <Field label="Rights start (UTC)"><input type="datetime-local" name="validFrom" required/></Field>}
        {endRule === 'dated' && <Field label="Rights expiry (UTC)" hint="Use the exact cutoff in the permission. An expired permission cannot authorize new use."><input type="datetime-local" name="expiresAt" required/></Field>}
      </div>
      <p className="muted">If the terms are missing or unclear, close this review and obtain the permission first.</p>
      <label className="check-field"><input name="ownerAttested" type="checkbox" required/>I inspected the evidence and verified that it covers this exact export, the selected placements, paid-use choice and validity period.</label>
      <p className="note"><ShieldCheck size={17}/><span>This records your rights review. Product fidelity and the final publication package still need their own approval.</span></p>
      <details><summary>Exact export reference</summary><p className="preserve-lines">{exportHash || 'File fingerprint not available'}</p></details>
      <div className="dialog-actions"><button type="button" className="button" onClick={close}>Cancel review</button><button className="button primary" type="submit" disabled={busy || !exactExportAvailable}>{busy ? 'Saving…' : 'Record usage rights'}</button></div>
    </form>
  </Dialog>;
}
